// Secure logging utility for Pochoclo.tv
// Prevents sensitive data exposure while maintaining debugging capabilities

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: any;
}

class SecureLogger {
  private isDevelopment: boolean;
  private isDebugMode: boolean;

  constructor() {
    this.isDevelopment = import.meta.env?.DEV || false;
    this.isDebugMode = import.meta.env?.VITE_DEBUG === 'true' || false;
  }

  // Sanitize sensitive data from objects
  private sanitizeData(data: any): any {
    if (typeof data !== 'object' || data === null) {
      return data;
    }

    const sensitiveKeys = [
      'password', 'token', 'key', 'secret', 'auth', 'api_key',
      'supabaseUrl', 'supabaseAnonKey', 'supabaseServiceKey',
      'VITE_SUPABASE_URL', 'VITE_SUPABASE_ANON_KEY',
      'authorization', 'bearer', 'jwt'
    ];

    const sanitized = { ...data };

    for (const [key, value] of Object.entries(sanitized)) {
      const lowerKey = key.toLowerCase();
      
      if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
        if (typeof value === 'string' && value.length > 0) {
          sanitized[key] = '[REDACTED]';
        } else {
          sanitized[key] = '[REDACTED]';
        }
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = this.sanitizeData(value);
      }
    }

    return sanitized;
  }

  // Check if environment variable exists without exposing value
  configStatus(name: string, value?: string): { configured: boolean; name: string } {
    return {
      name,
      configured: !!(value || (typeof process !== 'undefined' && process.env?.[name]))
    };
  }

  // Development-only logging
  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment && this.isDebugMode) {
      console.log(`🐛 [DEBUG] ${message}`, context ? this.sanitizeData(context) : '');
    }
  }

  // General info logging (development only)
  info(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      console.log(`ℹ️ [INFO] ${message}`, context ? this.sanitizeData(context) : '');
    }
  }

  // Warning logging (always shown)
  warn(message: string, context?: LogContext): void {
    console.warn(`⚠️ [WARN] ${message}`, context ? this.sanitizeData(context) : '');
  }

  // Error logging (always shown)
  error(message: string, context?: LogContext): void {
    console.error(`🚨 [ERROR] ${message}`, context ? this.sanitizeData(context) : '');
  }

  // Configuration status logging (development only)
  configCheck(configs: Record<string, any>): void {
    if (this.isDevelopment) {
      const statusCheck: Record<string, boolean> = {};
      
      for (const [key, value] of Object.entries(configs)) {
        statusCheck[`${key}Configured`] = !!value;
      }
      
      console.log('🔧 Configuration status:', statusCheck);
    }
  }

  // Performance logging (development only)
  performance(operation: string, duration: number): void {
    if (this.isDevelopment) {
      console.log(`⏱️ [PERF] ${operation}: ${duration.toFixed(2)}ms`);
    }
  }

  // Security event logging (always shown)
  security(event: string, context?: LogContext): void {
    console.warn(`🔒 [SECURITY] ${event}`, context ? this.sanitizeData(context) : '');
  }
}

// Export singleton instance
export const logger = new SecureLogger();

// Convenience functions
export const logDebug = (message: string, context?: LogContext) => logger.debug(message, context);
export const logInfo = (message: string, context?: LogContext) => logger.info(message, context);
export const logWarn = (message: string, context?: LogContext) => logger.warn(message, context);
export const logError = (message: string, context?: LogContext) => logger.error(message, context);
export const logSecurity = (event: string, context?: LogContext) => logger.security(event, context);

// Environment variable helper
export const checkConfig = (name: string, value?: string) => logger.configStatus(name, value);